import React from 'react';
import { CheckIcon, EyeIcon, LayoutGridIcon, MessageSquareTextIcon, SlidersHorizontalIcon, TargetIcon, TimerIcon } from 'lucide-react';
import { SectionHeading } from '../ui/SectionHeading';
import { CtaButton } from '../ui/CtaButton';
import { COVER_IMAGE, coreGuideFacts } from '../../data/site';
import { guideFeatures, GuideFeatureIcon, teachingSteps } from '../../data/guideFeatures';
const icons: Record<GuideFeatureIcon, typeof TargetIcon> = {
  target: TargetIcon,
  message: MessageSquareTextIcon,
  layout: LayoutGridIcon,
  timer: TimerIcon,
  sliders: SlidersHorizontalIcon,
  eye: EyeIcon
};
export function CoreContent() {
  return <section aria-labelledby="content-title" className="bg-white py-20 text-ink lg:py-24" data-id="element-131" data-mp="134ol8blfm4qe">
      <div className="mx-auto max-w-page px-5 lg:px-8" data-id="element-132" data-mp="1864540bvi577">
        <SectionHeading id="content-title" tone="light" eyebrow="The core guide" lead="Everything you need" highlight="to teach the unit" data-id="element-133" data-mp="u5td2tytukkg" />

        <article className="mt-12 grid items-center gap-8 rounded-xl border border-[#E5E7EB] p-6 sm:p-8 md:grid-cols-[220px_1fr] md:gap-10 lg:p-10" data-id="element-134" data-mp="1fpg6toyk19u5">
          <div className="mx-auto w-40 md:w-full" data-id="element-135" data-mp="1bqp3v17eqxz4">
            <img src={COVER_IMAGE} alt="Cover of the Handball Class Ready guide" loading="lazy" className="block h-auto w-full shadow-[0_18px_36px_-16px_rgba(0,0,0,0.45)]" data-id="element-136" data-mp="1ponje9xgt0g7" />
          </div>
          <div data-id="element-137" data-mp="wxdjjureqvax">
            <p className="text-[13px] font-bold uppercase tracking-[0.16em] text-gold-ink" data-id="element-138" data-mp="s0ww72s18kyw">Core guide · Digital PDF</p>
            <h3 className="mt-2 font-display text-[30px] font-bold uppercase leading-[1.02] sm:text-[38px]" data-id="element-139" data-mp="16pt2auaj3nwv">
              12 Ready-to-Teach School Handball Lessons
            </h3>
            <p className="mt-3 text-base leading-relaxed text-[#374151] sm:text-lg" data-id="element-140" data-mp="e8yvngcdzxec">
              One visual guide that takes your class from first catches to small-sided games, one 40-minute lesson
              at a time.
            </p>
            <ul className="mt-6 grid gap-x-6 gap-y-2.5 sm:grid-cols-2" data-id="element-141" data-mp="29blpalo763k9">
              {coreGuideFacts.map(fact => <li key={fact} className="flex gap-2.5 text-base" data-id="element-142" data-mp="2e9dj5l8ejzr3">
                  <CheckIcon aria-hidden="true" strokeWidth={3} className="mt-1 h-4 w-4 shrink-0 text-go-hover" data-id="element-143" data-mp="2j39h4noo1mm9" />
                  {fact}
                </li>)}
            </ul>
          </div>
        </article>

        <h3 className="mt-16 text-center font-display text-[30px] font-bold uppercase leading-none sm:text-[36px]" data-id="element-144" data-mp="1qfdi3cbmjimb">
          In every lesson
        </h3>
        <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-5" data-id="element-145" data-mp="1zdxww2e2d859">
          {guideFeatures.map(feature => {
          const Icon = icons[feature.icon];
          return <li key={feature.label} className="flex gap-4 rounded-xl border border-[#E5E7EB] p-6" data-id="element-146" data-mp="3atw0hqqh31z3">
                <span aria-hidden="true" className="flex h-12 w-12 shrink-0 items-center justify-center" style={{
              backgroundColor: feature.bg,
              color: feature.fg
            }} data-id="element-147" data-mp="3fro2jdku04h0">
                  <Icon className="h-6 w-6" strokeWidth={2.25} data-id="element-148" data-mp="3kxui5hxqbusc" />
                </span>
                <div data-id="element-149" data-mp="3g59ruqe17ztk">
                  <h4 className="text-[13px] font-bold uppercase tracking-[0.16em] text-gold-ink" data-id="element-150" data-mp="3g4hxfiw6884v">{feature.label}</h4>
                  <p className="mt-1.5 text-base leading-relaxed text-[#374151]" data-id="element-151" data-mp="3c36d9sw2gf42">{feature.text}</p>
                </div>
              </li>;
        })}
        </ul>

        <div className="mt-16 rounded-xl bg-mint px-6 py-10 sm:px-10" data-id="element-152" data-mp="325i9kdghfx9r">
          <h3 className="text-center font-display text-[30px] font-bold uppercase leading-none sm:text-[36px]" data-id="element-153" data-mp="2x8rwlyzz5hsk">
            How it works
          </h3>
          <ol className="mt-8 grid gap-8 md:grid-cols-3 md:gap-8" data-id="element-154" data-mp="vgddwxplw9b3">
            {teachingSteps.map((step, index) => <li key={step.title} className="flex gap-4 md:flex-col md:gap-3" data-id="element-155" data-mp="re8okb66xyu9" data-mpi={index}>
                <span aria-hidden="true" className="flex h-12 w-12 shrink-0 items-center justify-center bg-forest font-display text-[26px] font-bold text-gold" data-id="element-156" data-mp="yhyo4ef6ezfl">
                  {index + 1}
                </span>
                <div data-id="element-157" data-mp="ph0jbtwwynvy">
                  <h4 className="font-display text-[24px] font-bold uppercase leading-tight" data-id="element-158" data-mp="pjmmzigooj8w">
                    <span className="sr-only" data-id="element-159" data-mp="tkq4saiakpcr">Step {index + 1}: </span>
                    {step.title}
                  </h4>
                  <p className="mt-1 text-base leading-relaxed text-[#374151]" data-id="element-160" data-mp="kip93i2pibd7">{step.text}</p>
                </div>
              </li>)}
          </ol>
        </div>

        <div className="mt-12 text-center" data-id="element-161" data-mp="5rgcuq4atrr3">
          <CtaButton target="offer" className="w-full sm:w-auto" data-id="element-162" data-mp="atfhs4foswq5">
            Get the lesson pack
          </CtaButton>
        </div>
      </div>
    </section>;
}